import type { Booking, TableSessionAvailability } from '../types/booking.types'
import { BookingStatus } from '../types/booking.types'
import type { PoolTable, TableType } from '../types/table.types'
import {
    SLOT_MINUTES,
    addMinutesToIso,
    formatSlotTime,
    minutesBetween,
    minutesOfDay,
    parseIso,
    timeAtDayRatio,
    todayIsoDate,
    toLocalIso,
} from './bookingGridUtils'

export const BOOKING_GROUP_ORDER: TableType[] = ['VIP', 'PYRAMID', 'POOL', 'SNOOKER']

export function inferBookingDateMode(date: string): 'today' | 'tomorrow' | 'custom' {
    const today = todayIsoDate()
    if (date === today) {
        return 'today'
    }
    if (date === addDaysToIsoDate(today, 1)) {
        return 'tomorrow'
    }
    return 'custom'
}

export function orderAvailableTableTypes(tables: Pick<PoolTable, 'type'>[]): TableType[] {
    const present = new Set(tables.map(table => table.type))
    return BOOKING_GROUP_ORDER.filter(type => present.has(type))
}

export const TIMELINE_COLORS = {
    free: '#BBF7D0',
    pending: '#FDE68A',
    confirmed: '#FECACA',
    session: '#FCA5A5',
} as const

export type HorizontalSegmentKind = 'FREE' | 'PENDING' | 'CONFIRMED' | 'SESSION'

export interface HorizontalSegment {
    kind: HorizontalSegmentKind
    startTime: string
    endTime: string
    booking?: Booking
    label?: string
}

export interface TimelinePressResult {
    segment: HorizontalSegment
    clickedTime: string
}

export type TimelineBlock =
    | { type: 'FREE'; startTime: string; endTime: string; durationMinutes: number }
    | {
          type: 'BOOKED'
          startTime: string
          endTime: string
          durationMinutes: number
          booking: Booking
          status: 'PENDING' | 'CONFIRMED'
      }
    | {
          type: 'SESSION'
          startTime: string
          endTime: string
          durationMinutes: number
          session: TableSessionAvailability
      }

interface RawInterval {
    startMs: number
    endMs: number
    kind: HorizontalSegmentKind
    booking?: Booking
    session?: TableSessionAvailability
}

export const DEFAULT_BOOK_DURATION_MINUTES = 60

export function defaultBookDuration(
    freeDurationMinutes: number,
    slotMinutes = SLOT_MINUTES,
    preferred = DEFAULT_BOOK_DURATION_MINUTES
): number {
    const capped = Math.min(preferred, freeDurationMinutes)
    return snapToSlotMinutes(capped, slotMinutes)
}

export function dayBoundsIso(date: string, clubOpen: string, clubClose: string): { dayStart: string; dayEnd: string } {
    return {
        dayStart: `${date}T${clubOpen}:00`,
        dayEnd: `${date}T${clubClose}:00`,
    }
}

export function snapToSlotMinutes(totalMinutes: number, slotMinutes = SLOT_MINUTES): number {
    return Math.max(slotMinutes, Math.round(totalMinutes / slotMinutes) * slotMinutes)
}

export function ceilToSlot(iso: string, slotMinutes = SLOT_MINUTES): string {
    const d = parseIso(iso)
    const truncated = new Date(d)
    truncated.setSeconds(0, 0)
    const remainder = truncated.getMinutes() % slotMinutes
    if (remainder === 0 && d.getSeconds() === 0 && d.getMilliseconds() === 0) {
        return toLocalIso(truncated)
    }
    truncated.setMinutes(truncated.getMinutes() + (slotMinutes - remainder))
    return toLocalIso(truncated)
}

export function floorToSlot(iso: string, slotMinutes = SLOT_MINUTES): string {
    const d = parseIso(iso)
    const truncated = new Date(d)
    truncated.setSeconds(0, 0)
    const remainder = truncated.getMinutes() % slotMinutes
    truncated.setMinutes(truncated.getMinutes() - remainder)
    return toLocalIso(truncated)
}

export function groupTablesByType(tables: PoolTable[]): { type: TableType; tables: PoolTable[] }[] {
    const byType = new Map<TableType, PoolTable[]>()
    for (const table of tables) {
        const list = byType.get(table.type) ?? []
        list.push(table)
        byType.set(table.type, list)
    }
    return BOOKING_GROUP_ORDER.filter((type) => (byType.get(type)?.length ?? 0) > 0).map((type) => ({
        type,
        tables: [...(byType.get(type) ?? [])].sort((a, b) => a.id - b.id),
    }))
}

export function bookingsForTableOnDate(bookings: Booking[], tableId: number, date: string): Booking[] {
    return bookings.filter((b) => {
        if (b.tableId !== tableId) return false
        if (b.status !== BookingStatus.PENDING && b.status !== BookingStatus.CONFIRMED) return false
        return b.startTime.slice(0, 10) === date || overlapsDay(b.startTime, b.durationMinutes, date)
    })
}

/** Future or ongoing PENDING/CONFIRMED bookings for one table (end time not yet passed), sorted by start. */
export function upcomingBookingsForTable(
    bookings: Booking[],
    tableId: number,
    nowIso: string,
    options?: { dateIso?: string }
): Booking[] {
    const nowMs = parseIso(nowIso).getTime()
    const dateIso = options?.dateIso
    return bookings
        .filter((b) => {
            if (b.tableId !== tableId) return false
            if (b.status !== BookingStatus.PENDING && b.status !== BookingStatus.CONFIRMED) return false
            if (dateIso && b.startTime.slice(0, 10) !== dateIso && !overlapsDay(b.startTime, b.durationMinutes, dateIso)) {
                return false
            }
            const endMs = parseIso(b.startTime).getTime() + b.durationMinutes * 60000
            return endMs > nowMs
        })
        .sort((a, b) => parseIso(a.startTime).getTime() - parseIso(b.startTime).getTime())
}

const BOOKED_IMMINENT_MINUTES = 60

export type TableDisplayBookedReason = 'imminent' | 'overdue'

/** CONFIRMED booking reason for display-only "Booked" on an IDLE table. */
export function tableDisplayBookedReason(
    bookings: Booking[],
    tableId: number,
    nowIso: string
): TableDisplayBookedReason | null {
    const nowMs = parseIso(nowIso).getTime()
    const imminentCutoffMs = nowMs + BOOKED_IMMINENT_MINUTES * 60000

    let hasOverdue = false
    let hasImminent = false

    for (const booking of bookings) {
        if (booking.tableId !== tableId) continue
        if (booking.status !== BookingStatus.CONFIRMED) continue

        const startMs = parseIso(booking.startTime).getTime()
        const endMs = startMs + booking.durationMinutes * 60000
        if (endMs <= nowMs) continue

        if (startMs <= nowMs) {
            hasOverdue = true
        } else if (startMs <= imminentCutoffMs) {
            hasImminent = true
        }
    }

    if (hasOverdue) return 'overdue'
    if (hasImminent) return 'imminent'
    return null
}

function overlapsDay(startIso: string, durationMinutes: number, date: string): boolean {
    const start = parseIso(startIso)
    const end = new Date(start.getTime() + durationMinutes * 60000)
    const dayStart = parseIso(`${date}T00:00:00`)
    const dayEnd = parseIso(`${date}T23:59:59`)
    return start < dayEnd && end > dayStart
}

function bookingLabel(booking: Booking): string {
    return bookingCustomerName(booking)
}

/** Display name for a booking customer (guest or registered user). */
export function bookingCustomerName(booking: Booking): string {
    if (booking.guestName?.trim()) return booking.guestName.trim()
    if (booking.userName?.trim()) return booking.userName.trim()
    if (booking.user?.fullName?.trim()) return booking.user.fullName.trim()
    return ''
}

/** Phone on file for a booking (guest or registered user), when present. */
export function bookingCustomerPhone(booking: Booking): string {
    if (booking.guestPhone?.trim()) return booking.guestPhone.trim()
    if (booking.userPhone?.trim()) return booking.userPhone.trim()
    if (booking.user?.phone?.trim()) return booking.user.phone.trim()
    return ''
}

/** True when two half-open intervals [start, end) overlap. */
export function intervalsOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
    return minutesBetween(startA, endB) > 0 && minutesBetween(startB, endA) > 0
}

/** Active bookings on a table that overlap the given time range. */
export function findConflictingBookings(
    bookings: Booking[],
    tableId: number,
    startTime: string,
    endTime: string,
    excludeBookingId?: number | null
): Booking[] {
    return bookings.filter((b) => {
        if (b.tableId !== tableId) return false
        if (excludeBookingId != null && b.id === excludeBookingId) return false
        if (
            b.status !== BookingStatus.PENDING &&
            b.status !== BookingStatus.CONFIRMED
        ) {
            return false
        }
        const bookingEnd = addMinutesToIso(b.startTime, b.durationMinutes)
        return intervalsOverlap(startTime, endTime, b.startTime, bookingEnd)
    })
}

function getSessionInterval(
    session: TableSessionAvailability,
    dayStart: string,
    dayEnd: string,
    nowIso: string,
    slotMinutes: number
): RawInterval | null {
    const sessionStartMs = parseIso(session.sessionStart).getTime()
    const dayStartMs = parseIso(dayStart).getTime()
    const dayEndMs = parseIso(dayEnd).getTime()

    let blockEndIso: string
    if (session.firstBookableSlot) {
        blockEndIso = session.firstBookableSlot
    } else if (session.plannedEnd) {
        blockEndIso = ceilToSlot(session.plannedEnd, slotMinutes)
    } else {
        const currentSlotStart = floorToSlot(nowIso, slotMinutes)
        blockEndIso = addMinutesToIso(currentSlotStart, slotMinutes)
    }

    const blockEndMs = parseIso(blockEndIso).getTime()
    if (blockEndMs <= dayStartMs || sessionStartMs >= dayEndMs) return null

    return {
        startMs: Math.max(sessionStartMs, dayStartMs),
        endMs: Math.min(blockEndMs, dayEndMs),
        kind: 'SESSION',
        session,
    }
}

function collectRawIntervals(
    tableId: number,
    date: string,
    clubOpen: string,
    clubClose: string,
    bookings: Booking[],
    tableSessions: TableSessionAvailability[],
    slotMinutes: number
): RawInterval[] {
    const { dayStart, dayEnd } = dayBoundsIso(date, clubOpen, clubClose)
    const dayStartMs = parseIso(dayStart).getTime()
    const dayEndMs = parseIso(dayEnd).getTime()
    const nowIso = toLocalIso(new Date())
    const intervals: RawInterval[] = []

    for (const booking of bookingsForTableOnDate(bookings, tableId, date)) {
        const startMs = Math.max(parseIso(booking.startTime).getTime(), dayStartMs)
        const endMs = Math.min(
            parseIso(booking.startTime).getTime() + booking.durationMinutes * 60000,
            dayEndMs
        )
        if (endMs <= startMs) continue
        intervals.push({
            startMs,
            endMs,
            kind: booking.status === BookingStatus.PENDING ? 'PENDING' : 'CONFIRMED',
            booking,
        })
    }

    const session = tableSessions.find((s) => s.tableId === tableId)
    if (session) {
        const sessionInterval = getSessionInterval(session, dayStart, dayEnd, nowIso, slotMinutes)
        if (sessionInterval) intervals.push(sessionInterval)
    }

    intervals.sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs)
    return mergeOverlappingIntervals(intervals)
}

/**
 * Resolves overlaps by painting the highest-priority interval over each elementary span.
 * Bookings outrank an active session so a booking starting while a table is already running
 * (e.g. table started 01:00, booking 01:30) stays visible; the session is clipped around it.
 */
function mergeOverlappingIntervals(intervals: RawInterval[]): RawInterval[] {
    if (intervals.length === 0) return []
    const priority: Record<HorizontalSegmentKind, number> = {
        CONFIRMED: 4,
        PENDING: 3,
        SESSION: 2,
        FREE: 1,
    }
    const points = Array.from(
        new Set(intervals.flatMap((i) => [i.startMs, i.endMs]))
    ).sort((x, y) => x - y)
    const result: RawInterval[] = []
    for (let p = 0; p < points.length - 1; p++) {
        const startMs = points[p]
        const endMs = points[p + 1]
        let winner: RawInterval | null = null
        for (const interval of intervals) {
            if (interval.startMs > startMs || interval.endMs < endMs) continue
            if (!winner || priority[interval.kind] > priority[winner.kind]) winner = interval
        }
        if (!winner) continue
        const prev = result[result.length - 1]
        if (prev && prev.endMs === startMs && prev.booking === winner.booking && prev.session === winner.session && prev.kind === winner.kind) {
            prev.endMs = endMs
        } else {
            result.push({ ...winner, startMs, endMs })
        }
    }
    return result
}

export function buildHorizontalSegments(
    tableId: number,
    date: string,
    clubOpen: string,
    clubClose: string,
    bookings: Booking[],
    tableSessions: TableSessionAvailability[],
    slotMinutes = SLOT_MINUTES
): HorizontalSegment[] {
    const { dayStart, dayEnd } = dayBoundsIso(date, clubOpen, clubClose)
    const dayStartMs = parseIso(dayStart).getTime()
    const dayEndMs = parseIso(dayEnd).getTime()
    const raw = collectRawIntervals(tableId, date, clubOpen, clubClose, bookings, tableSessions, slotMinutes)
    const segments: HorizontalSegment[] = []
    let cursor = dayStartMs

    for (const interval of raw) {
        if (interval.startMs > cursor) {
            segments.push({
                kind: 'FREE',
                startTime: toLocalIso(new Date(cursor)),
                endTime: toLocalIso(new Date(interval.startMs)),
            })
        }
        segments.push({
            kind: interval.kind,
            startTime: toLocalIso(new Date(Math.max(interval.startMs, dayStartMs))),
            endTime: toLocalIso(new Date(Math.min(interval.endMs, dayEndMs))),
            booking: interval.booking,
            label: interval.booking
                ? bookingLabel(interval.booking) || undefined
                : interval.session?.tableName,
        })
        cursor = Math.max(cursor, interval.endMs)
    }

    if (cursor < dayEndMs) {
        segments.push({
            kind: 'FREE',
            startTime: toLocalIso(new Date(cursor)),
            endTime: dayEnd,
        })
    }

    return segments.filter((s) => minutesBetween(s.startTime, s.endTime) > 0)
}

export function buildTimelineBlocks(
    tableId: number,
    date: string,
    clubOpen: string,
    clubClose: string,
    bookings: Booking[],
    tableSessions: TableSessionAvailability[],
    slotMinutes = SLOT_MINUTES
): TimelineBlock[] {
    const horizontal = buildHorizontalSegments(
        tableId,
        date,
        clubOpen,
        clubClose,
        bookings,
        tableSessions,
        slotMinutes
    )
    const blocks: TimelineBlock[] = []

    for (const seg of horizontal) {
        const durationMinutes = minutesBetween(seg.startTime, seg.endTime)
        if (durationMinutes <= 0) continue

        if (seg.kind === 'FREE') {
            blocks.push({ type: 'FREE', startTime: seg.startTime, endTime: seg.endTime, durationMinutes })
        } else if (seg.kind === 'SESSION' && seg.label) {
            const session = tableSessions.find((s) => s.tableId === tableId)
            if (session) {
                blocks.push({
                    type: 'SESSION',
                    startTime: seg.startTime,
                    endTime: seg.endTime,
                    durationMinutes,
                    session,
                })
            }
        } else if (seg.booking) {
            blocks.push({
                type: 'BOOKED',
                startTime: seg.startTime,
                endTime: seg.endTime,
                durationMinutes,
                booking: seg.booking,
                status: seg.kind === 'PENDING' ? 'PENDING' : 'CONFIRMED',
            })
        }
    }

    return mergeAdjacentFreeBlocks(blocks)
}

function mergeAdjacentFreeBlocks(blocks: TimelineBlock[]): TimelineBlock[] {
    const result: TimelineBlock[] = []
    for (const block of blocks) {
        const prev = result[result.length - 1]
        if (block.type === 'FREE' && prev?.type === 'FREE') {
            result[result.length - 1] = {
                ...prev,
                endTime: block.endTime,
                durationMinutes: minutesBetween(prev.startTime, block.endTime),
            }
        } else {
            result.push({ ...block })
        }
    }
    return result
}

export function buildHourTickFractions(clubOpen: string, clubClose: string): number[] {
    const [openH, openM] = clubOpen.split(':').map(Number)
    const [closeH, closeM] = clubClose.split(':').map(Number)
    const openMinutes = openH * 60 + openM
    const closeMinutes = closeH * 60 + closeM
    const total = closeMinutes - openMinutes
    if (total <= 0) return [0]

    const fractions: number[] = []
    for (let m = openMinutes; m <= closeMinutes; m += 60) {
        fractions.push((m - openMinutes) / total)
    }
    return fractions
}

export function buildHourLabelsForHeader(clubOpen: string, clubClose: string): string[] {
    const [openH] = clubOpen.split(':').map(Number)
    const [closeH] = clubClose.split(':').map(Number)
    const labels: string[] = []
    for (let h = openH; h <= closeH; h++) {
        labels.push(`${h}:00`)
    }
    return labels
}

export function segmentColor(kind: HorizontalSegmentKind): string {
    switch (kind) {
        case 'FREE':
            return TIMELINE_COLORS.free
        case 'PENDING':
            return TIMELINE_COLORS.pending
        case 'CONFIRMED':
            return TIMELINE_COLORS.confirmed
        case 'SESSION':
            return TIMELINE_COLORS.session
        default:
            return TIMELINE_COLORS.free
    }
}

export function hitTestTimeline(
    segments: HorizontalSegment[],
    date: string,
    clubOpen: string,
    clubClose: string,
    ratio: number,
    slotMinutes = SLOT_MINUTES
): TimelinePressResult | null {
    const clickedTime = timeAtDayRatio(date, clubOpen, clubClose, ratio, slotMinutes)
    const clickMin = minutesOfDay(clickedTime)

    for (const seg of segments) {
        const segStartMin = minutesOfDay(seg.startTime)
        const segEndMin = minutesOfDay(seg.endTime)
        if (clickMin >= segStartMin && clickMin < segEndMin) {
            return { segment: seg, clickedTime }
        }
    }
    const last = segments[segments.length - 1]
    if (!last) return null
    return { segment: last, clickedTime }
}

export function formatTimeRange(startIso: string, endIso: string): string {
    return `${formatSlotTime(startIso)} – ${formatSlotTime(endIso)}`
}

export function formatDurationHours(minutes: number): string {
    if (minutes % 60 === 0) return `${minutes / 60}h`
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    if (h === 0) return `${m}m`
    return `${h}h ${m}m`
}

export function addDaysToIsoDate(date: string, days: number): string {
    const d = new Date(date + 'T12:00:00')
    d.setDate(d.getDate() + days)
    return d.toISOString().slice(0, 10)
}

export function formatDatePickerLabel(date: string): string {
    const d = new Date(date + 'T12:00:00')
    return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

export interface TimelineHighlightBand {
    leftPct: number
    widthPct: number
}

export function highlightBandFractions(
    date: string,
    clubOpen: string,
    clubClose: string,
    startTime: string,
    endTime: string
): TimelineHighlightBand | null {
    const { dayStart, dayEnd } = dayBoundsIso(date, clubOpen, clubClose)
    const totalMinutes = minutesBetween(dayStart, dayEnd)
    if (totalMinutes <= 0) return null

    const bandStart = startTime < dayStart ? dayStart : startTime
    const bandEnd = endTime > dayEnd ? dayEnd : endTime
    if (!bandStart || !bandEnd || bandStart >= bandEnd) return null

    const startOffset = minutesBetween(dayStart, bandStart)
    const bandMinutes = minutesBetween(bandStart, bandEnd)
    return {
        leftPct: (startOffset / totalMinutes) * 100,
        widthPct: Math.max((bandMinutes / totalMinutes) * 100, 0.5),
    }
}
